import React, {useState, useEffect} from 'react';
import type {List, Map} from 'immutable';

import notifier from '@albert-io/notifier';
import {Dialogue, LoadingSpinner} from '@albert-io/atomic';
import {query} from '@albert-io/json-api-framework/request/builder';
import {genericMandarkRequest, type ResourceType} from 'resources/mandark.resource';
import type {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';
import errorCodes from 'client/errorCodes';

import HardCapErrorModal from 'client/components/HardCapErrorModal/HardCapErrorModal.react';

import {
  LaunchContextNotFound,
  RegistrationNotFound,
  LaunchSessionCookieNotFound,
  GenericLmsImportError,
  ImportRosterError,
  InvalidPermissions,
  InvalidEmailDomain,
  SchoolEmailDomainNotFound
} from './ErrorStates';

import ImportRoster from './ImportRoster.react';
import ImportRosterSuccess from './ImportRosterSuccess.react';

interface Props extends PropsWithClassNameRequired {
  classroom: ClassroomModelV1;
  handleClose: () => void;
}

/**
 * The transport layer already reports server errors, and response-less network failures are
 * deliberately console-only — but a local TypeError (a bug in our own handling) would otherwise
 * vanish into the generic dialogue with no engineering signal.
 */
function notifyIfClientBug(e: unknown, message: string) {
  if (!(e as any)?.response && e instanceof TypeError) {
    notifier.notify(e, {message, tags: 'InviteByLMS'});
  }
}

const InviteByLMS = ({className, classroom, handleClose}: Props) => {
  const [component, setComponent] = useState(
    <Dialogue hideCloseBtn className={className} size='m' title=''>
      <Dialogue.Body>
        <LoadingSpinner size={3} />
      </Dialogue.Body>
    </Dialogue>
  );

  const syncClassroom = async (nullEmailStudents: List<any>) => {
    try {
      const classroomId = classroom.getId();
      // The query builder's `done()` is typed as Record<string, unknown>, but the
      // resulting config object is exactly the GenericRequestQuery shape that
      // genericMandarkRequest consumes. Cast at the boundary until the builder's
      // `done()` return type is tightened.
      const courseSyncQuery = query()
        .mandarkEndpoint(['lti', 'course_sync_v1', classroomId])
        .done() as Parameters<typeof genericMandarkRequest>[1];

      await genericMandarkRequest('post', courseSyncQuery, {});

      // courseSyncQuery doesn't return anything, so assume success
      setComponent(
        <ImportRosterSuccess
          handleClose={handleClose}
          className={className}
          classroomId={classroomId}
        />
      );
    } catch (e) {
      const error = (e as any)?.response?.body?.errors?.[0];
      if (error?.code === 'Elixir.DBConnection.ConnectionError') {
        setComponent(<RegistrationNotFound className={className} handleClose={handleClose} />);
      } else if (error?.code === 'Errors.BadRequest.InvalidEmailDomain') {
        // Expected error message: The provided email 'hotmail.com' failed email domain validation.
        // Note: This is _extremely_ fragile
        const messagePrefix = "The provided email '";
        const messageSuffix = "' failed email domain validation.";
        const message = error.detail;
        const invalidEmailDomain = message.substring(
          messagePrefix.length,
          message.indexOf(messageSuffix)
        );
        const emailDomains = classroom
          .getSchool()
          .getEmailDomains()
          .map((item) => item.getName())
          .toArray();
        setComponent(
          <InvalidEmailDomain
            className={className}
            handleClose={handleClose}
            invalidEmailDomain={invalidEmailDomain}
            classroomDomains={emailDomains}
          />
        );
      } else if (error?.code === 'Errors.BadRequest.LTI.SchoolEmailDomainNotFound') {
        setComponent(
          <SchoolEmailDomainNotFound
            className={className}
            handleClose={handleClose}
            nullEmailStudents={nullEmailStudents}
          />
        );
      } else if (error?.code === errorCodes.FORBIDDEN.EXCEEDED_HARD_CAP_LIMIT) {
        setComponent(<HardCapErrorModal onDismiss={handleClose} />);
      } else {
        notifyIfClientBug(e, 'LMS roster sync failed before reaching the server');
        setComponent(
          <ImportRosterError
            handleClose={handleClose}
            className={className}
            syncClassroom={() => syncClassroom(nullEmailStudents)}
          />
        );
      }
    }
  };

  useEffect(() => {
    const fetchClassroom = async () => {
      try {
        const platformSignInQuery = query()
          .mandarkEndpoint(['lti', 'auth', 'platform_sign_in'])
          .done() as Parameters<typeof genericMandarkRequest>[1];
        // this POST request will put the lti_platform_access_token cookie in the browser
        // which is used in the getClassroomQuery call
        await genericMandarkRequest('post', platformSignInQuery, {});

        const getClassroomQuery = query()
          .mandarkEndpoint(['lti', 'course_v1'])
          .done() as Parameters<typeof genericMandarkRequest>[1];
        // The /lti/course_v1 endpoint is non-JSON:API, so the response is an Immutable
        // Map (via fromJS) rather than a List collection. Narrow to ResourceType so
        // `.get('members')` resolves against the Map key, not List's numeric index.
        const getClassroomResponse = (await genericMandarkRequest(
          'get',
          getClassroomQuery,
          {}
        )) as ResourceType;

        // `members` is a dynamic LTI payload of nested Immutable Maps; each member is
        // accessed by string key, so type it as List<Map<string, any>>.
        const students = getClassroomResponse.get('members') as List<Map<string, any>>;

        // Immutable 3.8's `filter` predicate types `value` as optional and returns an
        // `Iterable` rather than the `List` it actually produces at runtime. Match that
        // optional-param signature (guarding the undefined case, which never occurs at
        // runtime) and cast the result back to List so it matches the List<any> consumers
        // (`syncClassroom` / SchoolEmailDomainNotFound).
        const currentNullEmailStudents = students.filter((student?: Map<string, any>) => {
          if (!student) {
            return false;
          }
          const isStudent =
            student
              .get('roles')
              .includes('http://purl.imsglobal.org/vocab/lis/v2/membership#Learner') &&
            student.get('fields').get('name') !== 'Test Student';

          if (isStudent) {
            const hasEmail = !!student.get('fields').get('email');
            return !hasEmail;
          }
          return false;
        }) as List<Map<string, any>>;

        setComponent(
          <ImportRoster
            handleClose={handleClose}
            syncClassroom={() => syncClassroom(currentNullEmailStudents)}
            // `students` is an Immutable List (iterated via .map in ImportRoster), but
            // ImportRoster currently types this prop as `any[]`. The honest fix is to
            // change that prop to List<Map<string, any>> in ImportRoster.react.tsx; cast
            // here at the boundary until that file is updated.
            students={students as unknown as any[]}
            className={className}
            seatsRemaining={classroom.getMeta().getCountOfSchoolLicenseSeatsRemaining()}
            shouldShowHardCapWarning={classroom.getSchool().isHardCap()}
          />
        );
      } catch (e) {
        const error = (e as any)?.response?.body?.errors?.[0];
        if (
          error?.code === 'Errors.BadRequest.LTI.LaunchSessionCookieNotFound' ||
          error?.code === 'Errors.BadRequest.LTI.InvalidJWT'
        ) {
          setComponent(
            <LaunchSessionCookieNotFound className={className} handleClose={handleClose} />
          );
        } else if (
          error?.code === 'Errors.ThirdPartyHTTPRequestError' &&
          error.detail?.error === 'invalid_scope'
        ) {
          setComponent(<InvalidPermissions className={className} handleClose={handleClose} />);
        } else if (error?.code === 'Errors.BadRequest.LTI.LaunchContextNotFound') {
          setComponent(<LaunchContextNotFound className={className} handleClose={handleClose} />);
        } else if (error?.code === 'Errors.BadRequest.LTI.RegistrationNotFound') {
          setComponent(<RegistrationNotFound className={className} handleClose={handleClose} />);
        } else {
          notifyIfClientBug(e, 'LMS launch lookup failed before reaching the server');
          setComponent(<GenericLmsImportError className={className} handleClose={handleClose} />);
        }
      }
    };

    fetchClassroom();
  }, []);

  return component;
};

export default InviteByLMS;
